import { useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import setUserPreferences from '@core/api/setUserPreferences';
import { AppVersion } from '@core/constants/appVersion';
import { ThemeContext } from '@core/providers/ThemeProvider';
import { CustomErroHandlerResponse, PostUserSettingsResponse } from '@core/types';
import isCustomErroHandlerResponse from '@core/utils/isCustomErrorHandlerResponse';
import { QUERY_KEY as USER_PREFERENCES_QUERY_KEY } from './useGetUserPreferences';

const useAppVersionPreference = () => {
  const { storedSettings } = useContext(ThemeContext);
  const queryClient = useQueryClient();

  // No stored value means the user has never opted into a version preference
  // — leave it undefined rather than defaulting it, so AppVersionGuard knows
  // not to force a redirect until the user actually chooses one.
  const preferredVersion: AppVersion | undefined = storedSettings?.appVersion;

  // Deliberately does NOT go through useSetUserSettings: that hook
  // optimistically writes to the USER_PREFERENCES cache before the network
  // request resolves, which caused AppVersionGuard (which reads this same
  // cache) to redirect the instant the switch was clicked, regardless of
  // whether the save actually succeeded. Here the cache — and therefore
  // preferredVersion/AppVersionGuard — only changes once the save is
  // confirmed.
  const setPreferredVersion = async (
    version: AppVersion,
  ): Promise<PostUserSettingsResponse | CustomErroHandlerResponse> => {
    const payload = JSON.stringify({ ...storedSettings, appVersion: version });
    const result = await setUserPreferences(payload);

    if (!isCustomErroHandlerResponse(result) && result?.success) {
      queryClient.setQueryData([USER_PREFERENCES_QUERY_KEY], (previous: any) => ({
        ...(previous || {}),
        settings: payload,
      }));
    }

    return result;
  };

  return { preferredVersion, setPreferredVersion };
};

export default useAppVersionPreference;
